import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import './i18n';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { initSyncEngine } from './lib/syncEngine';
import ErrorBoundary from './components/ErrorBoundary';
import { installPatches as installDebugLog, isEnabled as isDebugEnabled } from './lib/debugLog';
import { maybeResetDefaults } from './lib/defaultsReset';

// Roll forward to the current canonical defaults (dark theme + blue
// palette + IBM Plex font + Brian/Shakir voices) for any browser still
// on an older defaults version. One-shot guarded by a version stamp,
// so user-made choices afterward are not touched. Must run BEFORE the
// theme init below so it sees the cleared theme key and falls back to
// the new 'dark' default.
maybeResetDefaults();

// Install the on-device debug log BEFORE React mounts so boot-time
// errors (a chunk failure, a hook-order mismatch, anything that
// crashes the tree before the ErrorBoundary catches it) still land
// in the persisted ring buffer. The patches are idempotent and
// nearly free when the toggle is off — they patch console once and
// then push into a buffer that's only read on demand.
if (isDebugEnabled()) installDebugLog();

// Initialize theme before React renders so the loading page respects
// the chosen mode. Theme can be 'light' | 'dark' | 'black'; the saved
// value lives under 'routine-app-theme'. 'black' is a stricter OLED
// variant of dark — same `dark` class plus an `oled` flag.
const initializeTheme = () => {
  const savedTheme = localStorage.getItem('routine-app-theme');
  // No saved choice → 'dark' is the canonical default. Matches
  // ThemeContext's readInitialMode() so the boot paint and the React
  // hydration agree.
  const mode = savedTheme || 'dark';
  const root = document.documentElement;
  if (mode === 'dark' || mode === 'black') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
  if (mode === 'black') {
    root.classList.add('oled');
  } else {
    root.classList.remove('oled');
  }
};

// Initialize theme immediately
initializeTheme();

// Runtime chunk-load failure recovery: if a dynamic import or a
// lazy-loaded chunk fails (typical "stale precache" symptom after a
// PWA update), force a single network reload so the browser picks up
// the new bundle. sessionStorage guards against a reload loop —
// matches the index.html watchdog's KEY name.
const CHUNK_RECOVERY_KEY = 'pwa-recovery-attempted';
function reloadOnce() {
  if (sessionStorage.getItem(CHUNK_RECOVERY_KEY) === '1') return;
  sessionStorage.setItem(CHUNK_RECOVERY_KEY, '1');
  window.location.reload();
}
function isChunkLoadFailure(msg) {
  if (!msg) return false;
  return /Loading chunk\b|ChunkLoadError|Failed to fetch dynamically imported module|Importing a module script failed/i.test(msg);
}
window.addEventListener('error', (event) => {
  if (isChunkLoadFailure(event.message || event.error?.message)) reloadOnce();
});
window.addEventListener('unhandledrejection', (event) => {
  if (isChunkLoadFailure(event.reason?.message || String(event.reason))) reloadOnce();
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);

serviceWorkerRegistration.register({
  onUpdate: (registration) => {
    window.dispatchEvent(new CustomEvent('pwa:update-available', { detail: registration }));
  },
});

initSyncEngine();
